import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ShieldCheck, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./OverviewTab";

const LABELS = {
    not_started: "Not started", broker_engaged: "W&I broker engaged", quotes_requested: "Quotes requested",
    quotes_received: "Quotes received", insurer_selected: "Insurer selected", underwriting: "Underwriting",
    bound: "Policy bound", declined: "Declined by insurers", not_required: "Not required",
};
const FLOW = ["broker_engaged", "quotes_requested", "quotes_received", "insurer_selected", "underwriting", "bound"];
const day = (v) => (v ? String(v).slice(0, 10) : "");
const numOrNull = (v) => (v === "" || v == null ? null : parseFloat(v));

export default function WiTab({ dealId, deal }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [quote, setQuote] = useState({ insurer: "", policy_limit: "", retention: "", premium: "" });
    const ccy = deal.currency;

    const load = useCallback(async () => {
        const { data } = await api.get(`/wi/deal/${dealId}`);
        setData(data);
        const p = data.policy;
        setForm({
            status: p.status, wi_broker: p.wi_broker || "", insurer: p.insurer || "",
            policy_limit: p.policy_limit ?? "", retention: p.retention ?? "", premium: p.premium ?? "",
            nbi_requested_date: day(p.nbi_requested_date), underwriting_call_date: day(p.underwriting_call_date),
            expected_bind_date: day(p.expected_bind_date), bound_date: day(p.bound_date),
            exclusions: p.exclusions || "", notes: p.notes || "",
        });
    }, [dealId]);
    useEffect(() => { load(); }, [load]);

    const fail = (err) => {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : Array.isArray(d) ? "Please check the values entered" : err.message);
    };
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            ["status", "wi_broker", "insurer", "nbi_requested_date", "underwriting_call_date", "expected_bind_date", "bound_date", "exclusions", "notes"]
                .forEach((k) => { if (form[k] !== "" && form[k] != null) body[k] = form[k]; });
            ["policy_limit", "retention", "premium"].forEach((k) => { const n = numOrNull(form[k]); if (n != null) body[k] = n; });
            const { data } = await api.put(`/wi/deal/${dealId}`, body);
            setData(data); toast.success("W&I details saved");
        } catch (e) { fail(e); } finally { setSaving(false); }
    };
    const addQuote = async (e) => {
        e.preventDefault();
        if (!quote.insurer.trim()) return;
        try {
            await api.post(`/wi/deal/${dealId}/quotes`, {
                insurer: quote.insurer.trim(), policy_limit: numOrNull(quote.policy_limit),
                retention: numOrNull(quote.retention), premium: numOrNull(quote.premium) });
            setQuote({ insurer: "", policy_limit: "", retention: "", premium: "" });
            load();
        } catch (e2) { fail(e2); }
    };
    const act = async (fn) => { try { await fn(); await load(); } catch (e) { fail(e); } };

    if (!data) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    const d = data.derived;
    const stepIdx = FLOW.indexOf(form.status);

    return (
        <div className="space-y-4" data-testid="wi-tab">
            <div className="border border-border rounded-md bg-white p-6 space-y-5">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="font-display font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> Warranty &amp; indemnity insurance</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Tracks the W&amp;I process and terms only — your W&amp;I broker and the insurer do the work.</p>
                    </div>
                    {(d.rate_on_line_pct != null || d.retention_pct_of_limit != null) && (
                        <div className="text-right text-xs text-muted-foreground" data-testid="wi-derived">
                            {d.rate_on_line_pct != null && <div>Rate on line <b className="font-mono text-navy">{d.rate_on_line_pct}%</b></div>}
                            {d.retention_pct_of_limit != null && <div>Retention <b className="font-mono text-navy">{d.retention_pct_of_limit}%</b> of limit</div>}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap gap-1.5" data-testid="wi-steps">
                    {FLOW.map((s, i) => (
                        <button key={s} onClick={() => set("status")(s)} type="button"
                            className={`px-2.5 py-1 rounded-full text-xs border ${form.status === s ? "bg-foreground text-background" : i < stepIdx ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-muted-foreground hover:bg-neutral-50"}`}>
                            {i < stepIdx && <Check className="inline w-3 h-3 mr-1" />}{LABELS[s]}
                        </button>
                    ))}
                    <Select value={["declined", "not_required", "not_started"].includes(form.status) ? form.status : ""} onValueChange={set("status")}>
                        <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="Other status…" /></SelectTrigger>
                        <SelectContent>{["not_started", "declined", "not_required"].map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="W&I broker"><Input value={form.wi_broker} onChange={set("wi_broker")} data-testid="wi-broker-input" /></Field>
                    <Field label="Insurer"><Input value={form.insurer} onChange={set("insurer")} data-testid="wi-insurer-input" /></Field>
                    <Field label={`Policy limit (${ccy})`}><Input type="number" className="font-mono" value={form.policy_limit} onChange={set("policy_limit")} data-testid="wi-limit-input" /></Field>
                    <Field label={`Retention (${ccy})`}><Input type="number" className="font-mono" value={form.retention} onChange={set("retention")} /></Field>
                    <Field label={`Premium (${ccy})`}><Input type="number" className="font-mono" value={form.premium} onChange={set("premium")} data-testid="wi-premium-input" /></Field>
                    <Field label="NBI requested"><Input type="date" value={form.nbi_requested_date} onChange={set("nbi_requested_date")} /></Field>
                    <Field label="Underwriting call"><Input type="date" value={form.underwriting_call_date} onChange={set("underwriting_call_date")} /></Field>
                    <Field label="Expected bind"><Input type="date" value={form.expected_bind_date} onChange={set("expected_bind_date")} /></Field>
                </div>
                {form.status === "bound" && <Field label="Bound on"><Input type="date" className="w-48" value={form.bound_date} onChange={set("bound_date")} /></Field>}
                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Key exclusions"><Textarea rows={3} value={form.exclusions} onChange={set("exclusions")} /></Field>
                    <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={set("notes")} /></Field>
                </div>
                <Button onClick={save} disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="wi-save-btn">{saving ? "Saving…" : "Save W&I details"}</Button>
            </div>

            <div className="border border-border rounded-md bg-white p-6 space-y-3">
                <h3 className="font-display font-bold tracking-tight">Insurer quotes</h3>
                {data.quotes.length > 0 && (
                    <table className="w-full text-sm" data-testid="wi-quotes">
                        <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left py-2">Insurer</th><th className="text-right py-2">Limit</th><th className="text-right py-2">Retention</th>
                            <th className="text-right py-2">Premium</th><th className="text-right py-2">Rate on line</th><th className="pl-4 text-left py-2">Status</th><th /></tr></thead>
                        <tbody>
                            {data.quotes.map((q) => (
                                <tr key={q.id} className={`border-b border-border/60 ${q.status === "selected" ? "bg-amber-50/40" : ""}`} data-testid={`wi-quote-${q.id}`}>
                                    <td className="py-2 font-medium">{q.insurer}</td>
                                    <td className="py-2 text-right font-mono">{q.policy_limit != null ? fmtMoney(q.policy_limit, ccy) : "—"}</td>
                                    <td className="py-2 text-right font-mono">{q.retention != null ? fmtMoney(q.retention, ccy) : "—"}</td>
                                    <td className="py-2 text-right font-mono">{q.premium != null ? fmtMoney(q.premium, ccy) : "—"}</td>
                                    <td className="py-2 text-right font-mono">{q.derived.rate_on_line_pct != null ? `${q.derived.rate_on_line_pct}%` : "—"}</td>
                                    <td className="py-2 pl-4 text-xs capitalize">{q.status}</td>
                                    <td className="py-2 text-right whitespace-nowrap">
                                        {q.status === "selected" ? <span className="text-xs text-emerald-700">Selected</span> : (
                                            <>
                                                {q.status === "received" && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/wi/quotes/${q.id}/select`))} data-testid={`wi-select-${q.id}`}>Select</Button>}
                                                {q.status === "received" && <Button size="sm" variant="ghost" onClick={() => act(() => api.patch(`/wi/quotes/${q.id}`, { status: "declined" }))}>Decline</Button>}
                                            </>
                                        )}
                                        <Button size="icon" variant="ghost" onClick={() => confirm("Delete this quote?") && act(() => api.delete(`/wi/quotes/${q.id}`))}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <form onSubmit={addQuote} className="grid grid-cols-2 md:grid-cols-12 gap-2" data-testid="wi-quote-form">
                    <Input className="md:col-span-4" placeholder="Insurer" value={quote.insurer} onChange={(e) => setQuote((q) => ({ ...q, insurer: e.target.value }))} data-testid="wi-quote-insurer" />
                    <Input className="md:col-span-3 font-mono" type="number" placeholder="Limit" value={quote.policy_limit} onChange={(e) => setQuote((q) => ({ ...q, policy_limit: e.target.value }))} />
                    <Input className="md:col-span-2 font-mono" type="number" placeholder="Retention" value={quote.retention} onChange={(e) => setQuote((q) => ({ ...q, retention: e.target.value }))} />
                    <Input className="md:col-span-2 font-mono" type="number" placeholder="Premium" value={quote.premium} onChange={(e) => setQuote((q) => ({ ...q, premium: e.target.value }))} />
                    <Button type="submit" className="md:col-span-1 bg-foreground text-background hover:bg-neutral-800 hover:text-white"><Plus className="w-4 h-4" /></Button>
                </form>
            </div>
        </div>
    );
}
